import { Device, Trip, User } from "@fairfleet/geotab";
import { isWithinInterval } from "date-fns";
import { REASON_NA } from "@/constants/constants";
import { useDevicesByIds } from "@/hooks/devices/useDevicesByIds";
import { useTripAnnotationForDevices } from "@/hooks/tripAnnotations/useTripAnnotationsForDevices";
import { useTripsByDateRange } from "@/hooks/trips/useTripsByDateRange";
import { useUsersByIds } from "@/hooks/users/useUsersByIds";
import { mapQuery } from "@/utilities/query/mapQuery";
import { tripInterval } from "@/utilities/trip/tripInterval";

interface Args {
  users?: User[];
  devices?: Device[];
  reasons?: string[];
  toDate: Date;
  fromDate: Date;
}

export interface FilteredTrip extends Trip {
  reason: string | false;
}

export function useFilteredTrips({
  users: filterUsers,
  devices: filterDevices,
  reasons = [],
  toDate,
  fromDate,
}: Args) {
  const isNa = reasons.includes(REASON_NA);

  const trips = useTripsByDateRange({
    users: filterUsers,
    devices: filterDevices,
    toDate,
    fromDate,
    limit: 1000,
  });
  const tripDeviceIds = trips.data?.map(t => t.device.id);
  const tripDriverIds = trips.data?.map(t => t.driver?.id).filter((id): id is string => !!id);
  const { data: devices } = useDevicesByIds(tripDeviceIds, [
    "id",
    "name",
    "serialNumber",
    "customProperties",
  ]);
  const { data: rawUsers } = useUsersByIds(tripDriverIds);
  const users = rawUsers?.flat();
  const { data: annotations } = useTripAnnotationForDevices({
    toDate,
    fromDate,
    deviceIds: tripDeviceIds,
  });

  return mapQuery(trips, trips => {
    return trips
      .map(trip => {
        const interval = tripInterval(trip);
        const annotation = annotations?.find(
          a => isWithinInterval(a.dateTime, interval) && a.device.id == trip.device.id,
        );

        if (isNa) {
          if (annotation) return;
        } else if (reasons.length > 0) {
          if (!annotation) return;
          if (!reasons.includes(annotation.text)) return;
        }

        return {
          ...trip,
          reason: annotation?.text,
          device: devices?.find(d => d.id === trip.device?.id) ?? trip.device,
          driver: users?.find(u => u.id === trip.driver?.id) ?? trip.driver,
        } as FilteredTrip;
      })
      .filter(t => !!t);
  });
}
